import React, { useMemo, useState } from "react";
import { AutocompleteInline, Menu } from "@puzzle/ui";
import { styled } from "@puzzle/theme";
import { CaretDown } from "@puzzle/icons";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { useSize } from "react-use";
import { InactiveCompanyFragment } from "components/companies/graphql.generated";
import { Route } from "lib/routes";
import useSelf from "components/users/useSelf";
import Link from "next/link";
import { useAppRouter } from "lib/useAppRouter";

const Button = styled("button", {
  border: "1px solid transparent",
  appearance: "none",
  background: "none",
  width: "100%",
  padding: "$1h",
  fontWeight: "$bold",
  fontSize: "$body",
  lineHeight: "16px",
  letterSpacing: "$bodyXS",
  color: "$text-neutral-base",
  cursor: "pointer",
  userSelect: "none",
  outline: "none",
  textAlign: "left",
  whiteSpace: "nowrap",
  overflow: "hidden",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "$1",

  "&:hover, &:focus": {
    color: "$text-neutral-moreHighlight",
  },

  "& > *:first-child": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: "0",
  },

  "&:disabled": {
    pointerEvents: "none",
  },

  "&:not(:disabled)": {
    borderColor: "$outline-neutral-base",
    borderRadius: "40px",
  },
});

export const CompanyMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { companies, company } = useActiveCompany<true>();
  const activeCompanyCurrentId = company?.id;

  const hasManyCompanies = companies.length > 1;
  const { router, isFirmRoute } = useAppRouter();

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [companies]);

  const onAdmin = isFirmRoute(router.asPath);
  const { self } = useSelf();
  const showAdminPortalLink = self?.firmMembership?.isOwner;
  const allowSelect = showAdminPortalLink || hasManyCompanies;

  const [trigger, { width }] = useSize(
    <Button disabled={!allowSelect}>
      <span>
        {onAdmin ? "Admin portal" : company?.name || (hasManyCompanies ? "Select Company" : "")}
      </span>
      {allowSelect && <CaretDown />}
    </Button>
  );

  if (!hasManyCompanies && !showAdminPortalLink) {
    return trigger;
  }

  const widthOfClearButton = 42;

  return (
    <Menu
      trigger={trigger}
      open={isOpen}
      onOpenChange={setIsOpen}
      css={{ width: width - widthOfClearButton }}
    >
      {hasManyCompanies && (
        <AutocompleteInline<InactiveCompanyFragment, false, true, false>
          open={isOpen}
          options={sortedCompanies}
          getOptionKey={(o) => o.id}
          getOptionLabel={(o) => o.name || ""}
          value={sortedCompanies.find((c) => c.id === activeCompanyCurrentId) || undefined}
          isOptionEqualToValue={(option, value) => {
            return option.id === value?.id;
          }}
          onChange={(_, selectedCompany) => {
            if (selectedCompany?.id) {
              const { pathname, query } = router;
              const newQuery = { ...query, companyId: selectedCompany.id };
              router.push({ pathname, query: newQuery });
              setIsOpen(false);
            }
          }}
          css={{ width: width - widthOfClearButton }}
        />
      )}
      {showAdminPortalLink && (
        <>
          {hasManyCompanies && <Menu.Separator />}
          <Menu.Group>
            <Link href={Route.firmBookkeepers} passHref legacyBehavior>
              <Menu.Item
                as="a"
                aria-selected={onAdmin}
                css={{
                  '&[aria-selected="true"]': {
                    color: "$text-neutral-highlight !important",
                    backgroundColor: "$surface-neutral-base",
                  },
                }}
                onClick={() => setIsOpen(false)}
              >
                Admin portal
              </Menu.Item>
            </Link>
          </Menu.Group>
        </>
      )}
    </Menu>
  );
};
